import React, { useState, useEffect } from "react";
import { Box, Button, useToast, CircularProgress, CircularProgressLabel, Text, useDisclosure, Collapse } from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import SearchableStoreSelect from "../../../Components/SearchableStoreSelect";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import AlohaPDFComponent from "../Files/AlohaPDFComponent";
import { getFormatterDate } from "../../../helpers/date";
import { handleAuthError } from "../../../utils/auth";
import {
  processAlohaHeaderReading,
  processAlohaSalesData,
  proccessAlohaDiscount,
  processAlohaGrossBreakDown,
  processAlohaCashierBreakDown,
  processAlohaPaymentData,
  processAlohaNonSalesData,
  processAlohaNonSalesDataWithTenderDesc,
  processAlohaBillNOQTY,
  processAlohaNonSales,
} from "../../../helpers/eodReport";
import axios from "axios";

const blink = keyframes`
0% {opacity: 1;}
50% {opacity: 0;}
100% {opacity: 1;}
`;

const dateCurr = new Date();

const SalesCN = ({ color }) => {
  const [reqField, setReqField] = useState({
    date: new Date(),
    branch: "",
    locCode: "",
    storecode: "",
    costcenter: "",
  });

  const role = localStorage.getItem("role");
  const toast = useToast();
  const { isOpen: controlsOpen, onToggle: toggleControls } = useDisclosure({ defaultIsOpen: true });
  const navigate = useNavigate();
  const [dataLoading, setDataLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Preparing report...");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [data, setData] = useState();
  const [stores, setStores] = useState([]);
  const [userAssignedStores, setUserAssignedStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);

  const handleDateChange = (date) => {
    setReqField((prev) => {
      return { ...prev, date: date };
    });
  };

  const handleBranchChange = (e) => {
    const storeList = role === "store" ? filteredStores : stores;
    const selectedStore = storeList.find(
      (store) => store.storecode === e.target.value,
    );

    if (selectedStore && selectedStore.StoreName) {
      setReqField((prev) => ({
        ...prev,
        branch: selectedStore.StoreName,
        locCode: selectedStore.locCode,
        storecode: selectedStore.storecode,
        costcenter: selectedStore.costcenter || selectedStore.storecode,
      }));
    }
  };

  useEffect(() => {
    const fetchStores = async () => {
      setDataLoading(true);
      try {
        // For CN brand, we'll use brandId 7
        const resStores = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/maintenance/getBrands`,
          {
            brandId: 7, // CN brand ID
          },
        );
        const uniqueStores = (resStores.data || []).filter(
          (s, i, arr) => i === arr.findIndex((x) => x.locCode === s.locCode),
        );
        setStores(uniqueStores);

        // Get user's assigned stores
        const storeName = localStorage.getItem("storeName");
        let assignedStores = [];

        if (storeName) {
          try {
            // Try to parse as JSON array first
            const parsed = JSON.parse(storeName);
            assignedStores = Array.isArray(parsed) ? parsed : [storeName];
          } catch {
            // If parsing fails, it's a regular string
            assignedStores = [storeName];
          }
        }

        setUserAssignedStores(assignedStores);

        // Filter stores to only show assigned ones
        const userStores = uniqueStores.filter((store) =>
          assignedStores.includes(store.StoreName),
        );
        setFilteredStores(userStores);

        if (role === "store") {
          // If user has only one store, auto-select it
          if (userStores.length === 1) {
            const store = userStores[0];
            if (store && store.StoreName) {
              setReqField((prev) => ({
                ...prev,
                branch: store.StoreName,
                locCode: store.locCode,
                storecode: store.storecode,
                costcenter: store.costcenter || store.storecode,
              }));
            }
          }
          // If user has multiple stores, leave dropdown enabled for selection
        }
        setDataLoading(false);
      } catch (e) {
        // Check for authentication errors first
        if (e.response && e.response.status === 401) {
          handleAuthError(navigate, toast);
          return;
        }

        toast({
          title: "Error",
          position: "top-right",
          description: "Failed to fetch stores",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setDataLoading(false);
      }
    };
    fetchStores();
  }, [role, toast, navigate]);

  const handleGenerateReport = async (date, branch) => {
    const newDate = getFormatterDate(date);
    const currDate = getFormatterDate(dateCurr);

    if (currDate < newDate) {
      toast({
        title: "Error",
        position: "top-right",
        description: "Date not Available.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (reqField.storecode === "") {
      toast({
        title: "Error",
        position: "top-right",
        description: "Please select a store.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    try {
      setDataLoading(true);
      // Helper function to check response type
      const isDataAvailable = (data) => Array.isArray(data) && data.length > 0;
      setLoadingProgress(0);
      let _step = 0;
      const advanceProgress = () => setLoadingProgress(Math.round(++_step / 11 * 100));
      // Header
      advanceProgress();
      setLoadingMessage("Fetching header readings...");
      const resHeader = await axios.get(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getHeaderReading.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resHeader?.data))
        throw new Error("No header data found");
      const { totalGrossAmt, totalvat, openingData, closingData } =
        processAlohaHeaderReading(resHeader.data);
      // Sales Data
      advanceProgress();
      setLoadingMessage("Fetching gross sales data...");
      const resSales = await axios.get(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getGrossSaleswithVat.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      console.log("resSales", resSales);
      if (!isDataAvailable(resSales?.data))
        throw new Error("No sales data found");
      const { salesData } = processAlohaSalesData(resSales.data);
      // Discount Data
      advanceProgress();
      setLoadingMessage("Fetching discount data...");
      const resDiscount = await axios.get(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getDiscount.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resDiscount?.data))
        throw new Error("No discount data found");
      const { dataDiscount, dataDiscountVAT, dataDiscountVATE } =
        proccessAlohaDiscount(resDiscount.data);

      // Gross Breakdown
      advanceProgress();
      setLoadingMessage("Fetching sales by channel...");
      const resGrossBreakDown = await axios.get(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getSaleswPerChannelithVat.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resGrossBreakDown?.data))
        throw new Error("No Gross Breakdown data found.");
      const {
        dataGrossBreakDownReturned,
        totalGrossBreakDownVAT,
        totalGrossBreakDownVATE,
      } = processAlohaGrossBreakDown(resGrossBreakDown.data);

      // Payment Breakdown
      advanceProgress();
      setLoadingMessage("Fetching payment breakdown...");
      const resPaymentData = await axios.post(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getPaymentData.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resPaymentData?.data))
        throw new Error("No payment breakdown data found.");
      const {
        totalPaymentCash,
        totalPaymentNonCash,
        paymentDataReturned,
        ChangeAmount,
        TenderAmount,
        VoidPaymentAmount,
        VoidPaymentCount,
        totalNonCashQty,
        NonSalesPaymentDataReturned,
        totalNonSalesCount,
      } = processAlohaPaymentData(resPaymentData.data);

      // Cashier Breakdown
      advanceProgress();
      setLoadingMessage("Fetching cashier accountability...");
      const resCashierBreakDown = await axios.post(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getCashAccountability.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resCashierBreakDown?.data))
        throw new Error("No Cashier Breakdown data found.");
      const {
        cashierBreakDownReturned,
        totalCashierAmount,
        totalShortOver,
        shortOverDataReturned,
        totalShortOverNonCash,
        shortOverNonCashDataReturned,
        CMCash,
        CMFloat,
        CMOverShort,
        CMPickUp,
      } = processAlohaCashierBreakDown(resCashierBreakDown.data);

      // BillNoQTY
      advanceProgress();
      setLoadingMessage("Fetching bill count & quantity...");
      const resBillQtyNo = await axios.post(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getBillNoAndQty.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resBillQtyNo?.data))
        throw new Error("No BillNoQTY data found.");
      const { TCQty, OrderQty } = processAlohaBillNOQTY(resBillQtyNo.data);

      // Non Misc
      advanceProgress();
      setLoadingMessage("Fetching non-sales items...");
      const resMisc = await axios.get(
        `${process.env.REACT_APP_ALOHA_API_URL}/getMisc.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resMisc?.data))
        throw new Error("No Non Sales data found.");
      const { NonSalesDataReturned } = processAlohaNonSalesData(resMisc.data);

      // Non Cash with tenderDesk
      advanceProgress();
      setLoadingMessage("Fetching non-cash tenders...");
      const resNonCashTenderDesk = await axios.get(
        `${process.env.REACT_APP_ALOHA_API_URL}/getNonCash.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resNonCashTenderDesk?.data))
        throw new Error("No misc data found.");
      const { NonSalesWithTenderDesc, totalNonSales, totalNonSalesCounter } =
        processAlohaNonSalesDataWithTenderDesc(resNonCashTenderDesk.data);
      //Non Sales
      advanceProgress();
      setLoadingMessage("Fetching void & non-sales data...");
      const resNonSales = await axios.get(
        `${
          process.env.REACT_APP_ALOHA_API_URL
        }/getNonsales.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
      );
      if (!isDataAvailable(resNonSales?.data))
        throw new Error("No Non Cash with tenderDesk data found.");
      const { nonSalesData } = processAlohaNonSales(resNonSales.data);

      // Scrap Sales (Order Data)
      advanceProgress();
      setLoadingMessage("Fetching order data...");
      const resOrder = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_API_URL
        }/aloha/getorder.aspx?brand=CN&qlik=${
          reqField.storecode
        }&datedfolder=${newDate.replace(/-/g, "")}`,
        { withCredentials: true },
      );
      const orderData = isDataAvailable(resOrder?.data) ? resOrder.data : [];

      // Setting data after successful API responses
      setData({
        nonSalesData,
        currDate: currDate,
        BizDate: newDate,
        NonSalesWithTenderDesc,
        totalNonSales,
        totalNonSalesCounter,
        orderData,
        NonSalesDataReturned,
        cashierBreakDownReturned,
        totalCashierAmount,
        totalShortOver,
        shortOverDataReturned,
        totalShortOverNonCash,
        shortOverNonCashDataReturned,
        CMCash,
        CMFloat,
        CMOverShort,
        CMPickUp,
        totalPaymentCash,
        totalPaymentNonCash,
        paymentDataReturned,
        ChangeAmount,
        TenderAmount,
        VoidPaymentAmount,
        VoidPaymentCount,
        totalNonCashQty,
        NonSalesPaymentDataReturned,
        totalNonSalesCount,
        dataDiscount,
        dataDiscountVAT,
        dataDiscountVATE,
        salesData,
        totalGrossAmt,
        totalvat,
        openingData,
        closingData,
        dataGrossBreakDownReturned,
        totalGrossBreakDownVAT,
        totalGrossBreakDownVATE,
        PosId: reqField.storecode,
        TCQty,
        OrderQty,
        storeName: reqField.branch,
        storeCode: reqField.costcenter,
      });
      setDataLoading(false);
    } catch (e) {
      setDataLoading(false);

      // Check for authentication errors first
      if (e.response && e.response.status === 401) {
        handleAuthError(toast, navigate);
        return;
      }

      toast({
        title: "Error",
        position: "top-right",
        description: e.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      h="calc(100vh - 60px)"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Mobile controls toggle */}
      <Box
        display={{ base: "flex", lg: "none" }}
        justifyContent="space-between"
        alignItems="center"
        px={4}
        py={2}
        mt={2}
        bg="gray.100"
        borderRadius="md"
        cursor="pointer"
        onClick={toggleControls}
        flexShrink={0}
      >
        <Text fontSize="sm" fontWeight="semibold" color="gray.600">
          Report Controls
        </Text>
        <ChevronDownIcon
          transition="transform 0.2s"
          transform={controlsOpen ? "rotate(180deg)" : "none"}
        />
      </Box>
      <Collapse in={controlsOpen} animateOpacity>
        <Box
          mt={{ base: 0, lg: 2 }}
          mb={0}
          pl={6}
          py={4}
          display="flex"
          gap={4}
          alignItems="center"
          flexDirection={{ base: "column", lg: "row" }}
          flexWrap={{ base: "wrap", lg: "nowrap" }}
          bg="gray.100"
          borderTopRadius={{ base: 0, lg: "md" }}
          borderBottomRadius="md"
          flexShrink={0}
        >
        <SearchableStoreSelect
          options={(role === "store" ? filteredStores : stores).map(
            (store) => ({
              value: store.storecode,
              label: store.StoreName,
            }),
          )}
          value={reqField.storecode}
          onChange={(val) => handleBranchChange({ target: { value: val } })}
          isDisabled={
            (role === "store" && userAssignedStores.length <= 1) || dataLoading
          }
          placeholder={
            role === "store" && userAssignedStores.length <= 1
              ? reqField.branch || "Loading..."
              : "Select Branch"
          }
        />

        <Box
          border="1px"
          borderColor="gray.300"
          borderRadius="md"
          display="flex"
          justifyContent="center"
          alignItems="center"
          bg="white"
          boxShadow="sm"
          h="40px"
          px={3}
        >
          <DatePicker
            name="date"
            disabled={dataLoading}
            minDate={new Date(2025, 3, 1)}
            maxDate={new Date()}
            selected={reqField.date}
            onChange={(date) => handleDateChange(date)}
            className="custom-date-picker"
            customInput={
              <div style={{ position: "relative" }}>
                <input
                  className="custom-date-picker-input"
                  value={reqField.date.toLocaleDateString()}
                  readOnly
                />
                <img
                  src="/calendar.svg"
                  className="calendar-icon"
                  alt="Calendar Icon"
                  style={{
                    position: "absolute",
                    right: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "20px",
                    height: "auto",
                  }}
                />
              </div>
            }
          />
        </Box>

        <Button
          bg="gray.700"
          color="white"
          size="md"
          onClick={() => handleGenerateReport(reqField.date, reqField.branch)}
          isDisabled={dataLoading}
          _hover={{ bg: "gray.600" }}
          boxShadow="sm"
        >
          Generate Report
        </Button>

        <Button
          bg="gray.700"
          color="white"
          size="md"
          _hover={{ bg: "gray.600" }}
          boxShadow="sm"
          isDisabled={!data || dataLoading}
        >
          {data ? (
            <PDFDownloadLink
              document={<AlohaPDFComponent data={data} />}
              fileName={`EODReport-${reqField.branch}-${getFormatterDate(reqField.date)}.pdf`}
            >
              {({ loading }) =>
                loading ? "Loading document..." : "Download Report"
              }
            </PDFDownloadLink>
          ) : (
            "Download Report"
          )}
        </Button>
      </Box>
      </Collapse>

      {/* PDF Viewer */}
      <Box mt={0} mb={0} height={{ base: "60vh", md: "80vh" }}>
        {dataLoading ? (
          <Box
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress value={loadingProgress} color="blue.500" trackColor="gray.200" size="80px">
              <CircularProgressLabel>{loadingProgress}%</CircularProgressLabel>
            </CircularProgress>
            <Text
              css={{ animation: `${blink} 1s linear infinite` }}
              marginLeft={4}
            >
              {loadingMessage}
            </Text>
          </Box>
        ) : (
          <PDFViewer
            showToolbar={false}
            style={{ width: "100%", height: "100%" }}
          >
            {data && <AlohaPDFComponent data={data} />}
          </PDFViewer>
        )}
      </Box>
    </Box>
  );
};

export default SalesCN;
